'use client';

import { type ReactNode, useCallback, useEffect, useRef } from 'react';

import { Timestamp } from '@ps/functions/firebase/firestore';
import { useBehaviorSubjectUpdater } from '@ps/functions/hooks/useBehaviorSubject';
import { useCreation } from '@ps/functions/hooks/useCreation';
import type { CommunityChannelMessageAttachment } from '@ps/models/lib/community-channel';
import { useCurrentUserId } from '@ps/ui/contexts/auth';
import { useCurrentUserProfile } from '@ps/ui/contexts/user-profile';
import { BehaviorSubject, Subject } from 'rxjs';

import { ChannelMessageSubscriber } from '../../functions/message/ChannelMessageSubscriber';
import { fetchChannelMessages } from '../../functions/message/fetchChannelMessages';
import { sendChannelMessage } from '../../functions/message/sendChannelMessage';
import { fetchThreadReplies } from '../../functions/thread/fetchThreadReplies';
import { appDateToDate } from '../../functions/utils/appDate';
import { useChannelMessageMediaUploader } from '../../hooks/useChannelMessageMediaUploader';
import type { ChannelMessageMediaInfo } from '../../types/ChannelMessageMediaInfo';
import type {
  ChannelMessageLocalAttachment,
  ExtendedCommunityChannelMessage,
  SendChannelMessageParams,
} from '../../types/ExtendedCommunityChannelMessage';
import { useCommunityContext } from '../community/useCommunityContext';
import {
  CommunityMessageContext,
  type CommunityMessageContextValue,
  type CommunityMessageState,
} from './context';

const kChannelPageSize = 30;
const kThreadPageSize = 50;

const kInitialState: CommunityMessageState = {
  isLoading: true,
  isLoadingMore: false,
  hasMore: true,
  cursor: undefined,
  error: undefined,
};

interface Props {
  channelId: string;
  /**
   * Thread anchor id — when set, the provider serves replies under
   * this message (ordered oldest-first, append-on-send). When
   * omitted, serves top-level channel messages (newest-first,
   * prepend-on-send).
   */
  parentMessageId?: string;
  /**
   * Page size override. Defaults to 30 for a channel / 50 for a thread; the
   * inline replies panel passes a small value (5) so "Show More Replies" paces
   * the reveal, matching upstream.
   */
  pageSize?: number;
  children?: ReactNode;
}

/**
 * Per-channel-or-thread message provider. Web port of PopshopRN's
 * `CommunityMessageProvider`
 * (`flows/community/components/message/message-provider/index.tsx`).
 *
 * Owns:
 *
 * - `messages$` — the message list as the UI consumes it. The
 *   initial page comes from the search API, then Firestore live
 *   updates fold on top.
 * - `state$` — loading / pagination / error.
 * - `medias$` + `fileUploader` — in-flight upload tracking for
 *   message attachments. Survives past send success so a retry
 *   reuses already-uploaded entries.
 * - `subscriber` — `ChannelMessageSubscriber` instance scoped to
 *   this channel (and optionally `parentMessageId` for threads).
 *
 * Send pipeline:
 *
 * 1. `sendMessage(params)` builds the optimistic bubble, prepends or
 *    appends to `messages$` (depending on channel vs thread mode),
 *    enqueues every attachment file on the uploader, then fires
 *    `internalSendMessage(clientMessageId, params)` in the
 *    background.
 * 2. `internalSendMessage` waits for every attachment to reach
 *    `source.type === 'remote'`, posts the channel-message request
 *    with the resolved URLs, then strips `isSending` from the
 *    bubble. On failure it stamps `sendingError` and keeps
 *    `sendingParams` for retry.
 * 3. `resendMessage(message)` reruns `internalSendMessage` with the
 *    bubble's existing `sendingParams`. Already-uploaded entries on
 *    `medias$` are reused; `error`-state ones are re-enqueued.
 */
export function CommunityMessageProvider({
  channelId,
  parentMessageId,
  pageSize: pageSizeProp,
  children,
}: Props) {
  const { creatorId } = useCommunityContext();
  const currentUserId = useCurrentUserId();
  const currentUserProfile = useCurrentUserProfile();

  const isThread = parentMessageId != null;
  const pageSize =
    pageSizeProp ?? (isThread ? kThreadPageSize : kChannelPageSize);
  const sortMode: 'desc' | 'asc' = isThread ? 'asc' : 'desc';

  const messages$ = useCreation(
    () => new BehaviorSubject<ExtendedCommunityChannelMessage[]>([]),
    [],
  );
  const state$ = useCreation(
    () => new BehaviorSubject<CommunityMessageState>(kInitialState),
    [],
  );
  const medias$ = useCreation(
    () => new BehaviorSubject<ChannelMessageMediaInfo[]>([]),
    [],
  );
  const pinnedMessagesInvalidated$ = useCreation(() => new Subject<void>(), []);

  const updateMessages = useBehaviorSubjectUpdater(messages$);
  const updateState = useBehaviorSubjectUpdater(state$);
  const updateMedias = useBehaviorSubjectUpdater(medias$);

  const fileUploader = useChannelMessageMediaUploader(creatorId, medias$);

  const subscriber = useCreation(
    () =>
      creatorId && channelId
        ? new ChannelMessageSubscriber(creatorId, channelId, parentMessageId)
        : undefined,
    [creatorId, channelId, parentMessageId],
  );

  // Latest seeded `createdAt` — the Firestore listener anchors here
  // so it only receives messages newer than the search-API seed.
  const lastTimestampRef = useRef<Date | undefined>(undefined);

  // ─── Fetch + paginate ────────────────────────────────────────────

  const fetchPage = useCallback(
    async (cursor: string | undefined, reset: boolean) => {
      if (!creatorId || !channelId) return;
      updateState(prev => ({
        ...prev,
        isLoading: cursor == null,
        isLoadingMore: cursor != null,
        error: undefined,
      }));
      try {
        const result = isThread
          ? await fetchThreadReplies(creatorId, channelId, parentMessageId!, {
              pageSize,
              cursor,
            })
          : await fetchChannelMessages(creatorId, channelId, {
              pageSize,
              cursor,
            });
        const rows = isThread
          ? (result as { replies: ExtendedCommunityChannelMessage[] }).replies
          : (result as { messages: ExtendedCommunityChannelMessage[] })
              .messages;
        const nextCursor = result.cursor;

        updateMessages(current => {
          // Reconcile by clientMessageId — preserves optimistic rows
          // that were inserted between the fetch starting and
          // landing.
          if (reset) {
            return sortMessages(rows, sortMode);
          }
          const seen = new Map(
            current.map(message => [message.clientMessageId, message]),
          );
          const merged = [...current];
          for (const row of rows) {
            if (!seen.has(row.clientMessageId)) merged.push(row);
          }
          return sortMessages(merged, sortMode);
        });
        updateState({
          isLoading: false,
          isLoadingMore: false,
          hasMore: nextCursor != null && rows.length >= pageSize,
          cursor: nextCursor,
          error: undefined,
        });

        if (rows.length > 0) {
          const newestCreatedAt =
            sortMode === 'desc'
              ? rows[0]?.createdAt
              : rows[rows.length - 1]?.createdAt;
          if (newestCreatedAt) {
            const candidate = appDateToDate(newestCreatedAt);
            if (
              !lastTimestampRef.current ||
              candidate > lastTimestampRef.current
            ) {
              lastTimestampRef.current = candidate;
            }
          }
        } else if (!lastTimestampRef.current) {
          lastTimestampRef.current = new Date();
        }
      } catch (error: unknown) {
        updateState(prev => ({
          ...prev,
          isLoading: false,
          isLoadingMore: false,
          error: error instanceof Error ? error : new Error(String(error)),
        }));
      }
    },
    [
      channelId,
      creatorId,
      isThread,
      pageSize,
      parentMessageId,
      sortMode,
      updateMessages,
      updateState,
    ],
  );

  // Initial fetch when channel/thread changes.
  useEffect(() => {
    lastTimestampRef.current = undefined;
    updateMessages([]);
    updateState(kInitialState);
    void fetchPage(undefined, true);
  }, [channelId, parentMessageId, fetchPage, updateMessages, updateState]);

  const loadMore = useCallback(() => {
    const current = state$.getValue();
    if (current.isLoading || current.isLoadingMore || !current.hasMore) return;
    void fetchPage(current.cursor, false);
  }, [fetchPage, state$]);

  const refresh = useCallback(() => {
    void fetchPage(undefined, true);
  }, [fetchPage]);

  // ─── Firestore subscription ──────────────────────────────────────

  useEffect(() => {
    if (!subscriber) return;
    // Anchor on the newest seeded message so we only receive new
    // arrivals; falls back to "now" when the seed is empty.
    const startAfter = lastTimestampRef.current ?? new Date();
    return subscriber.subscribeToChannelMessages(
      { startAfter },
      {
        onNewMessage: incoming => mergeIncoming(incoming, 'added'),
        onMessageUpdated: incoming => mergeIncoming(incoming, 'modified'),
        onMessageDeleted: messageId => {
          updateMessages(current =>
            current.filter(message => message.id !== messageId),
          );
        },
      },
    );

    function mergeIncoming(
      incoming: ExtendedCommunityChannelMessage,
      _kind: 'added' | 'modified',
    ) {
      updateMessages(current => {
        const index = current.findIndex(
          message =>
            (incoming.clientMessageId &&
              message.clientMessageId === incoming.clientMessageId) ||
            message.id === incoming.id,
        );
        if (index === -1) {
          return sortMessages([...current, incoming], sortMode);
        }
        const next = [...current];
        // Strip local-only sending flags on reconciliation.
        next[index] = {
          ...next[index],
          ...incoming,
          isSending: undefined,
          sendingError: undefined,
          sendingParams: undefined,
        };
        return sortMessages(next, sortMode);
      });
      // Once the confirmation lands, this message's pending medias
      // are no longer needed — the bubble reads from
      // `message.attachments` from here on.
      if (incoming.clientMessageId) {
        updateMedias(list => {
          const remaining: ChannelMessageMediaInfo[] = [];
          for (const entry of list) {
            if (entry.clientMessageId === incoming.clientMessageId) {
              if (entry.source.type === 'local') {
                URL.revokeObjectURL(entry.source.localFileUri);
              }
              continue;
            }
            remaining.push(entry);
          }
          return remaining;
        });
      }
    }
  }, [subscriber, sortMode, updateMedias, updateMessages]);

  // ─── Send pipeline ───────────────────────────────────────────────

  const internalSendMessage = useCallback(
    async (
      clientMessageId: string,
      params: SendChannelMessageParams,
    ): Promise<void> => {
      if (!creatorId || !channelId) {
        throw new Error('Channel context unavailable');
      }

      const wantedAttachments = params.attachments ?? [];
      const links = params.links ?? [];

      let uploaded: CommunityChannelMessageAttachment[] = [];

      if (wantedAttachments.length > 0) {
        uploaded = await waitForAttachments(
          medias$,
          fileUploader,
          clientMessageId,
          wantedAttachments,
        );
      }

      await sendChannelMessage({
        creatorId,
        channelId,
        clientMessageId,
        content: params.content,
        contentType: params.contentType,
        parentMessageId,
        attachments: uploaded.length ? uploaded : undefined,
        links: links.length ? links : undefined,
      });
    },
    [channelId, creatorId, fileUploader, medias$, parentMessageId],
  );

  const sendMessage = useCallback(
    async (params: SendChannelMessageParams): Promise<void> => {
      if (!creatorId || !channelId || !currentUserId) return;

      const clientMessageId = generateClientMessageId();
      const now = Timestamp.now();
      const optimistic: ExtendedCommunityChannelMessage = {
        id: clientMessageId,
        channelId,
        clientMessageId,
        rootMessageId: parentMessageId ?? clientMessageId,
        parentMessageId,
        level: parentMessageId ? 2 : 1,
        userId: currentUserId,
        profile: {
          firstName: currentUserProfile?.firstName ?? '',
          lastName: currentUserProfile?.lastName ?? '',
          username: currentUserProfile?.username ?? '',
          avatar: currentUserProfile?.avatar ?? '',
        } as ExtendedCommunityChannelMessage['profile'],
        content: {
          textContent: { type: params.contentType, content: params.content },
          links: params.links?.length ? params.links : undefined,
        },
        reactions: {},
        replyCount: 0,
        recentRepliedProfiles: [],
        deleted: false,
        createdAt: now,
        updatedAt: now,
        isSending: true,
        sendingParams: params,
      };

      updateMessages(current =>
        isThread ? [...current, optimistic] : [optimistic, ...current],
      );

      // Enqueue every attachment file on the uploader. The bridge
      // hook patches `medias$` as events fire; the send pipeline
      // below waits until every entry reaches `remote`.
      if (params.attachments?.length && fileUploader) {
        for (const attachment of params.attachments) {
          fileUploader.enqueue({
            context: { clientMessageId, key: attachment.id },
            type: attachment.contentType.startsWith('image/')
              ? 'image'
              : 'video',
            localFileUri: attachment.localFileUri,
            localFile: attachment.localFile,
            filename: attachment.filename,
            contentType: attachment.contentType,
          });
        }
      }

      try {
        await internalSendMessage(clientMessageId, params);
        // Clear the optimistic sending flag eagerly — the Firestore
        // confirmation will replace the bubble shortly, but the
        // spinner shouldn't linger.
        patchOptimistic(clientMessageId, {
          isSending: false,
          sendingError: undefined,
          sendingParams: undefined,
        });
      } catch (error: unknown) {
        patchOptimistic(clientMessageId, {
          isSending: false,
          sendingError:
            error instanceof Error ? error.message : 'Failed to send',
        });
      }
    },
    [
      channelId,
      creatorId,
      currentUserId,
      currentUserProfile,
      fileUploader,
      internalSendMessage,
      isThread,
      parentMessageId,
      updateMessages,
    ],
  );

  const resendMessage = useCallback(
    async (message: ExtendedCommunityChannelMessage): Promise<void> => {
      const params = message.sendingParams;
      if (!params) return;

      patchOptimistic(message.clientMessageId, {
        isSending: true,
        sendingError: undefined,
      });

      // Re-enqueue any error-state attachments. Already-`remote`
      // entries stay put and get reused.
      if (params.attachments?.length && fileUploader) {
        const current = medias$.getValue();
        for (const attachment of params.attachments) {
          const existing = current.find(entry => entry.key === attachment.id);
          if (existing?.source.type === 'remote') continue;
          fileUploader.enqueue({
            context: {
              clientMessageId: message.clientMessageId,
              key: attachment.id,
            },
            type: attachment.contentType.startsWith('image/')
              ? 'image'
              : 'video',
            localFileUri: attachment.localFileUri,
            localFile: attachment.localFile,
            filename: attachment.filename,
            contentType: attachment.contentType,
          });
        }
      }

      try {
        await internalSendMessage(message.clientMessageId, params);
        patchOptimistic(message.clientMessageId, {
          isSending: false,
          sendingError: undefined,
          sendingParams: undefined,
        });
      } catch (error: unknown) {
        patchOptimistic(message.clientMessageId, {
          isSending: false,
          sendingError:
            error instanceof Error ? error.message : 'Failed to send',
        });
      }
    },
    [fileUploader, internalSendMessage, medias$],
  );

  const removeMessage = useCallback(
    (message: ExtendedCommunityChannelMessage): void => {
      updateMessages(current =>
        current.filter(
          item => item.clientMessageId !== message.clientMessageId,
        ),
      );
      updateMedias(list => {
        const remaining: ChannelMessageMediaInfo[] = [];
        for (const entry of list) {
          if (entry.clientMessageId === message.clientMessageId) {
            if (entry.source.type === 'local') {
              URL.revokeObjectURL(entry.source.localFileUri);
            }
            continue;
          }
          remaining.push(entry);
        }
        return remaining;
      });
    },
    [updateMedias, updateMessages],
  );

  function patchOptimistic(
    clientMessageId: string,
    patch: Partial<ExtendedCommunityChannelMessage>,
  ): void {
    updateMessages(current =>
      current.map(message =>
        message.clientMessageId === clientMessageId
          ? { ...message, ...patch }
          : message,
      ),
    );
  }

  const value = useCreation<CommunityMessageContextValue>(
    () => ({
      messages$,
      state$,
      medias$,
      sendMessage,
      resendMessage,
      removeMessage,
      loadMore,
      refresh,
      pinnedMessagesInvalidated$,
    }),
    [
      messages$,
      state$,
      medias$,
      sendMessage,
      resendMessage,
      removeMessage,
      loadMore,
      refresh,
      pinnedMessagesInvalidated$,
    ],
  );

  return (
    <CommunityMessageContext.Provider value={value}>
      {children}
    </CommunityMessageContext.Provider>
  );
}

function sortMessages(
  list: ExtendedCommunityChannelMessage[],
  mode: 'desc' | 'asc',
): ExtendedCommunityChannelMessage[] {
  return [...list].sort((a, b) => {
    const am = a.createdAt.toMillis();
    const bm = b.createdAt.toMillis();
    if (am === bm) return 0;
    return mode === 'desc' ? bm - am : am - bm;
  });
}

function generateClientMessageId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `c_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Resolve the final `CommunityChannelMessageAttachment[]` for a send call. Watches
 * `medias$` for entries matching `clientMessageId` + every wanted
 * attachment key; resolves once they all reach `source.type ===
 * 'remote'`. Rejects if any entry transitions to `error` state.
 */
async function waitForAttachments(
  medias$: BehaviorSubject<ChannelMessageMediaInfo[]>,
  _fileUploader: ReturnType<typeof useChannelMessageMediaUploader> | undefined,
  clientMessageId: string,
  wantedAttachments: ChannelMessageLocalAttachment[],
): Promise<CommunityChannelMessageAttachment[]> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const settle = (
      outcome:
        | { kind: 'resolve'; value: CommunityChannelMessageAttachment[] }
        | { kind: 'reject'; error: unknown },
    ) => {
      if (settled) return;
      settled = true;
      // Defer the unsubscribe so the synchronous initial emission
      // (BehaviorSubject delivers the current value at subscribe
      // time) can complete before we touch the subscription
      // variable — which isn't assigned yet during that first call.
      queueMicrotask(() => subscription.unsubscribe());
      if (outcome.kind === 'resolve') resolve(outcome.value);
      else reject(outcome.error);
    };

    const tryResolve = () => {
      if (settled) return;
      const current = medias$
        .getValue()
        .filter(entry => entry.clientMessageId === clientMessageId);
      const resolved: CommunityChannelMessageAttachment[] = [];
      for (const wanted of wantedAttachments) {
        const entry = current.find(item => item.key === wanted.id);
        if (!entry) return; // Not registered yet — wait.
        if (entry.source.type === 'local') {
          if (entry.source.state.status === 'error') {
            settle({ kind: 'reject', error: entry.source.state.error });
            return;
          }
          return; // Still uploading.
        }
        resolved.push({
          filename: wanted.filename,
          contentType: entry.source.contentType,
          url: entry.source.url,
          objectId: entry.source.id,
        });
      }
      settle({ kind: 'resolve', value: resolved });
    };

    const subscription = medias$.subscribe(() => tryResolve());
  });
}
